<template>
  <section v-if="!hasVideoFilterApplied" class="empty-state-section">
    <v-row
      class="d-flex flex-column"
      no-gutters
      justify="center"
      align="center"
    >
      <img class="mb-4" src="@/assets/videosEmptyState.svg" width="220" />
      <span class="body-regular-large textType3--text">
        {{ $t('message.pages.analyticsV3.videoComparer.emptyState.title') }}
      </span>
      <span class="body-regular-small textType6--text mt-2 text-center">
        {{ $t('message.pages.analyticsV3.videoComparer.emptyState.subtitle') }}
      </span>
    </v-row>
  </section>
  <div v-else class="comparison--table-wrapper">
    <v-sheet
      class="comparison--fixed-row"
      :class="{ 'apply-box-shadow': hasVerticalScroll }"
    >
      <template v-if="loading">
        <v-row class="thumbnail-row comparison--table-header" no-gutters>
          <v-col cols="2" class="thumbnail-col"></v-col>
          <v-col v-for="n in 3" :key="n" cols="3" class="thumbnail-col">
            <v-row class="img-row" no-gutters>
              <v-skeleton-loader type="button"></v-skeleton-loader>
            </v-row>
            <v-row class="video-title-row--loader" no-gutters>
              <v-skeleton-loader type="text"></v-skeleton-loader>
            </v-row>
          </v-col>
          <v-col cols="1" class="thumbnail-col"></v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row
          v-if="displayItems.length > 0"
          class="thumbnail-row comparison--table-header"
          no-gutters
        >
          <v-col cols="2" class="thumbnail-col"></v-col>
          <v-col
            v-for="(item, index) in displayItems"
            :key="item.id || `placeholder-${index}`"
            class="thumbnail-col min-0"
            cols="3"
          >
            <template v-if="!item.isPlaceholder">
              <v-row class="img-row" no-gutters>
                <slot name="thumbnail" :row="item" :index="index">
                  <div class="default-thumbnail">
                    <!-- <v-icon size="40" color="white">mdi-play-circle</v-icon> -->
                  </div>
                </slot>
              </v-row>
              <span
                class="one-line-ellipsis body-bold-smaller color-neutral-20-dark"
                >{{ truncateMiddle(item.title) || `Vídeo ${index + 1}` }}</span
              >
            </template>
            <template v-else>
              <v-row
                class="add-video-row"
                @click="$root.$emit('openVideosFilterModal')"
                no-gutters
              >
                <v-btn icon small class="add-video-btn">
                  <v-icon size="20"> panda-add </v-icon>
                </v-btn>
              </v-row>
            </template>
          </v-col>
          <v-col cols="1" class="thumbnail-col"></v-col>
        </v-row>
      </template>
    </v-sheet>

    <div class="comparison--table-body">
      <div
        v-for="(metric, metricIndex) in metrics"
        :key="`metric-group-${metricIndex}`"
        class="metric-group"
        :class="{
          'metric-group-highlight': metricIndex % 2 == 0,
          'apply-box-shadow': expandedRows.includes(metricIndex),
        }"
      >
        <v-row
          no-gutters
          style="flex-wrap: nowrap; width: 100%"
          class="table-row metric-row"
          @click="toggleExpand(metricIndex)"
        >
          <v-col cols="2" class="table-col label-col" ref="firstCols">
            <div class="metric-label-container">
              <span class="body-bold-small color-neutral-20-dark">{{
                metric.label
              }}</span>
              <v-tooltip :max-width="'300px'" v-if="metric.info" top>
                <template #activator="{ attrs, on }">
                  <v-icon class="info-icon" size="12" v-bind="attrs" v-on="on">
                    panda-info-solid
                  </v-icon>
                </template>
                <span v-html="metric.info"></span>
              </v-tooltip>
            </div>
          </v-col>
          <template v-if="loading">
            <v-col
              v-for="n in 3"
              :key="n"
              cols="3"
              class="table-col metric-col"
            >
              <v-row class="metric-row--loader" no-gutters>
                <v-skeleton-loader type="text"></v-skeleton-loader>
              </v-row>
            </v-col>
          </template>
          <template v-else>
            <v-col
              v-for="(item, itemIndex) in displayItems"
              :key="`metric-${metricIndex}-item-${item.id || itemIndex}`"
              class="table-col metric-col"
              cols="3"
            >
              <div v-if="!item.isPlaceholder" class="metric-value-container">
                <slot :name="`${metric.prop}Cell`" :row="item" :metric="metric">
                  <div
                    :class="['metric-value', getValueClass(item, metric), ,]"
                  >
                    {{ formatValue(item[metric.prop], metric.type) }}
                  </div>
                </slot>
              </div>
            </v-col>
          </template>

          <v-col v-if="expandable" cols="1" class="expand-col">
            <v-spacer></v-spacer>
            <v-btn icon small class="expand-btn">
              <v-icon size="16">
                {{
                  expandedRows.includes(metricIndex)
                    ? 'panda-chev-up'
                    : 'panda-chev-down'
                }}
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-expand-transition>
          <div v-if="expandedRows.includes(metricIndex)">
            <v-row
              no-gutters
              style="flex-wrap: nowrap; width: 100%"
              class="expanded-content"
            >
              <v-card
                class="pa-4"
                flat
                style="width: 100%"
                :class="{ 'expanded-content-highlight': metricIndex % 2 == 0 }"
              >
                <slot
                  name="expanded-content"
                  :metric="metric"
                  :index="metricIndex"
                  :items="sortedItems"
                />
              </v-card>
            </v-row>
          </div>
        </v-expand-transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TableVideoComparer',
  props: {
    items: { type: Array, default: () => [] },
    metrics: {
      type: Array,
      required: true,
      validator: (m) => m.every((i) => i.prop && i.label),
    },
    maxItems: { type: Number, default: 3 },
    loading: { type: Boolean, default: false },
    expandable: { type: Boolean, default: true },
    hasVideoFilterApplied: { type: Boolean, default: true },
    rowHeight: { type: [Number, String], default: 48 },
    thumbnailRowHeight: { type: [Number, String], default: 150 },
    expandColumnWidth: { type: [Number, String], default: 60 },
    firstColumnWidth: { type: [Number, String], default: 180 },
    columnWidth: { type: [Number, String], default: 180 },
  },
  data() {
    return {
      sortColumn: null,
      sortOrder: 'asc',
      sortedItems: [],
      expandedRows: [],
      hasVerticalScroll: false,
    }
  },
  computed: {
    displayItems() {
      const items = [...this.sortedItems]
      const placeholders = []
      const itemsToPad = this.maxItems - items.length

      if (itemsToPad > 0) {
        for (let i = 0; i < itemsToPad; i++) {
          placeholders.push({
            id: `placeholder-${i}`,
            isPlaceholder: true,
          })
        }
      }
      return [...items, ...placeholders]
    },
    tableIntroTitleStep3() {
      return `${this.$t('message.pages.analyticsV3.tours.compare.step3.title')}`
    },
    tableIntroDescriptionStep3() {
      return `${this.$t(
        'message.pages.analyticsV3.tours.compare.step3.description'
      )}`
    },
    tableIntroTitleStep4() {
      return `${this.$t('message.pages.analyticsV3.tours.compare.step4.title')}`
    },
    tableIntroDescriptionStep4() {
      return `${this.$t(
        'message.pages.analyticsV3.tours.compare.step4.description'
      )}`
    },
    currentLocale() {
      return this.$i18next.language
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.sortedItems = [...newItems]
        if (this.sortColumn) {
          this.sortData()
        }

        this.updateIntroSteps()
      },
    },
    currentLocale() {
      // toda vez que trocar o idioma, refazemos o ghost
      this.updateIntroSteps()
    },
  },
  updated() {
    setTimeout(() => {
      this.checkVerticalScroll()
    }, 150)
  },
  methods: {
    truncateMiddle(str = '', max = 35) {
      if (str.length <= max) return str // curto o suficiente
      const keep = Math.floor((max - 3) / 2) // 3 = '...'
      return `${str.slice(0, keep)}...${str.slice(-keep)}`
    },
    checkVerticalScroll() {
      function checkVerticalScroll(el) {
        return el && el.scrollHeight > el.clientHeight
      }
      const $contentEl = document.querySelector(
        '.page-view__comparison .page-view__content_slot'
      )
      this.hasVerticalScroll = checkVerticalScroll($contentEl)
      return this.hasVerticalScroll
    },
    formatValue(value, type) {
      if (value == null) return '-'
      switch (type) {
        case 'percentage':
          return `${value}%`
        case 'number':
          return Number(value).toLocaleString('pt-BR')
        case 'bytes':
          return this.formatBytes(value)
        default:
          return value
      }
    },

    formatBytes(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    toggleExpand(index) {
      const expandedIndex = this.expandedRows.indexOf(index)

      if (expandedIndex > -1) {
        this.expandedRows.splice(expandedIndex, 1)
      } else {
        this.expandedRows.pop()
        this.expandedRows.push(index)
      }
      this.$emit('row-expand', { index, expanded: expandedIndex === -1 })
    },

    sortData() {
      this.sortedItems.sort((a, b) => {
        let valA = a[this.sortColumn]
        let valB = b[this.sortColumn]

        if (typeof valA === 'string') valA = valA.toLowerCase()
        if (typeof valB === 'string') valB = valB.toLowerCase()

        return 0
      })
    },
    updateIntroSteps() {
      if (!this.items.length) {
        return
      }

      this.$nextTick(() => {
        const cells = this.$el.querySelectorAll('.label-col')
        if (!cells.length) return

        let union = cells[0].getBoundingClientRect()
        cells.forEach((cell) => {
          const r = cell.getBoundingClientRect()
          union = {
            top: Math.min(union.top, r.top),
            left: Math.min(union.left, r.left),
            right: Math.max(union.right, r.right),
            bottom: Math.max(union.bottom, r.bottom),
          }
        })

        const ghostId = 'video-comparer-ghost'

        let ghost = document.getElementById(ghostId)

        if (!ghost) {
          ghost = document.createElement('div')
          ghost.id = ghostId
          document.body.appendChild(ghost)
        }

        const tableIntro = `<strong>${this.tableIntroTitleStep3}</strong><br><br>${this.tableIntroDescriptionStep3}`

        const expandIntro = `<strong>${this.tableIntroTitleStep4}</strong><br><br>${this.tableIntroDescriptionStep4}`

        Object.assign(ghost.style, {
          position: 'absolute',
          top: `${union.top + window.scrollY}px`,
          left: `${union.left + window.scrollX}px`,
          width: `${union.right - union.left}px`,
          height: `${union.bottom - union.top}px`,
          pointerEvents: 'none',
          zIndex: '9999',
        })
        ghost.setAttribute('data-intro', tableIntro)
        ghost.setAttribute('data-step', '3')
        ghost.setAttribute('data-position', 'right')

        document.body.appendChild(ghost)

        const expand = document.getElementsByClassName('expand-btn')[0]

        if (expand) {
          expand.setAttribute('data-intro', expandIntro)
          expand.setAttribute('data-step', '4')
          expand.setAttribute('data-position', 'left')
        }

        this.$root.$emit('videosComparerMounted')
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.comparison--table-wrapper {
  width: 100%;
  .comparison--table-header {
    max-width: 1540px;
    margin-left: auto;
    margin-right: auto;
  }
  .comparison--fixed-row {
    width: 100%;
    position: absolute;
    z-index: 16;
    background-color: varFigma(neutral-100-white);
    border-bottom: 1px solid varFigma(neutral-95-lightest);
  }
  .comparison--table-body {
    max-width: 1540px;
    margin-top: 86px;
    margin-left: auto;
    margin-right: auto;
  }
}

.apply-box-shadow {
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.15);
}

.thumbnail-row {
  display: flex;
  width: 100%;
  height: 86px;
  flex-direction: row;
  align-items: center;

  .thumbnail-col {
    width: 100%;
    height: 100%;
    padding: $spacing-04 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    text-align: center;

    .img-row {
      height: 40px;
      width: 66px;
    }
  }
}
.video-title-row--loader {
  width: 212px;
  .v-skeleton-loader::v-deep {
    display: contents;
    .v-skeleton-loader__text {
      height: 18px !important;
      border-radius: 8px !important;
    }
  }
}
.metric-row--loader {
  width: 84px;
  .v-skeleton-loader::v-deep {
    display: contents;
    .v-skeleton-loader__text {
      height: 16px !important;
      border-radius: 8px !important;
    }
  }
}
.img-row {
  .v-skeleton-loader::v-deep {
    .v-skeleton-loader__text {
      height: 40px !important;
      width: 66px !important;
      border-radius: 8px !important;
    }
  }
}
.color-neutral-20-dark {
  color: varFigma(neutral-20-dark);
}
.min-0 {
  min-width: 0;
}
.one-line-ellipsis {
  display: block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.metric-group-highlight {
  background-color: varFigma(neutral-99);
}
.expanded-content-highlight {
  background-color: varFigma(neutral-99);
}
.table-row {
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: start;
}
.table-row:hover {
  background-color: #0000000f;
  cursor: pointer;
}

.table-col {
  align-self: center;
  width: 100%;
  height: 100%;
  &.label-col {
    padding: $spacing-05 0 $spacing-05 $spacing-07;
  }
  &.metric-col {
    justify-items: center;
  }
}
.info-icon {
  margin-left: $spacing-03;
  margin-top: 2px;
  cursor: pointer;
  &.v-icon {
    color: varFigma(neutral-70);
  }
}
.expand-btn {
  margin: $spacing-05 $spacing-07;
  &.v-btn::before {
    background-color: transparent;
  }
  &.v-btn > .v-btn__content .v-icon {
    &.theme--light {
      color: #636578;
    }
    &.theme--dark {
      color: #b9bbc6;
    }
  }
}
.expand-col {
  display: flex;
  align-self: center;
  justify-items: center;
}
.add-video-row {
  align-items: center;
}
.add-video-btn {
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  border-radius: $spacing-03;
  background: varFigma(neutral-95-lightest);
}
.first-column-custom {
  width: 25%;
}
.metric-value-cell {
  width: 25%;
}
.metric-value-cell {
  padding: 15px;
  background-color: transparent;
}
.thumbnail-card {
  width: 25%;
  background-color: transparent;
}
.empty-state-section {
  display: grid;
  place-items: center;
  min-height: 450px;
  text-align: center;
}
.video-title {
  font-size: 12px;
  font-weight: 400;
  color: black;
  padding-top: 0.2rem;
}
.metric-label-cell {
  background-color: transparent;
}
.metric-label {
  cursor: pointer;
  font-weight: 500;
  color: #495057;
  font-size: 13px;
  background-color: transparent;
}
.metric-label-container {
  white-space: nowrap;
}
.placeholder-value {
  color: #636578;
  background-color: transparent;
}
.add-placeholder {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 0.7;
  }
}
.add-placeholder-text {
  color: #636578;
  font-size: 12px;
  font-weight: 500;
}
.button-card {
  background-color: transparent;
}
.expandable-card,
.first-column {
  background-color: transparent;
}
</style>
